import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Send, Clock, Building } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { contactInfo } from '../mock/data';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiry_type: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      inquiry_type: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact/inquiry`, formData);
      
      if (response.status === 200) {
        // Success - show success message
        setSubmitStatus('success');
        
        // Clear form after a brief delay
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            inquiry_type: '',
            subject: '',
            message: ''
          });
          setSubmitStatus(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inquiryTypes = [
    { value: 'halal_manufacturing', label: 'Halal Manufacturing (First Cuisine)' },
    { value: 'nonhalal_catering', label: 'Non-Halal Catering (PS Catering)' },
    { value: 'bakery_frozen', label: 'Bakery & Frozen Foods (FC Industries)' },
    { value: 'oem_services', label: 'OEM Manufacturing Services' },
    { value: 'therapeutic_diets', label: 'Therapeutic Diets & Specialized Foods' },
    { value: 'general', label: 'General Inquiry' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Ready to partner with Singapore's leading food manufacturing group? 
              Get in touch with us for all your food manufacturing and catering needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-gray-600">Choose the right contact for your specific needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {contactInfo.map((contact, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Building className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">{contact.type}</span>
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 mb-4 text-sm leading-tight">
                    {contact.company}
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <MapPin className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{contact.address}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* General Contact Information */}
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-r from-blue-50 to-red-50 border-2 border-blue-200">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">General Inquiries</h3>
                  <p className="text-gray-600">For any questions or business inquiries, reach out to us</p>
                </div>
                
                <div className="flex flex-col md:flex-row justify-center items-center gap-8">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-600 p-3 rounded-full">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Call Us</div>
                      <a href="tel:+6569581668" className="text-lg font-semibold text-blue-600 hover:text-blue-800">
                        +65 6958 1668
                      </a>
                    </div>
                  </div>
                  
                  <div className="hidden md:block h-12 w-px bg-gray-300"></div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="bg-red-600 p-3 rounded-full">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Email Us</div>
                      <a href="mailto:sales@firstcuisine.com.sg" className="text-lg font-semibold text-red-600 hover:text-red-800">
                        sales@firstcuisine.com.sg
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Send Us A Message</h2>
            <p className="text-lg text-gray-600">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </div>

          <Card className="shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@company.com"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+65 1234 5678"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inquiry_type">Type of Inquiry *</Label>
                  <Select onValueChange={handleSelectChange} required disabled={isSubmitting}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select the type of inquiry" />
                    </SelectTrigger>
                    <SelectContent>
                      {inquiryTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Brief subject of your inquiry"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please provide details about your inquiry, requirements, or questions..."
                    className="min-h-32"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <p className="font-semibold">✅ Success!</p>
                    <p className="text-sm">Thank you for your inquiry! We have received your message and will get back to you within 24 hours.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <p className="font-semibold">❌ Error</p>
                    <p className="text-sm">Failed to submit your inquiry. Please check your connection and try again.</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    type="submit" 
                    className={`flex-1 ${
                      isSubmitting 
                        ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setFormData({
                      name: '', email: '', phone: '', company: '', 
                      inquiry_type: '', subject: '', message: ''
                    })}
                    className="flex-1 sm:flex-none"
                    disabled={isSubmitting}
                  >
                    Clear Form
                  </Button>
                </div>
              </form>

              {/* Response Time Info */}
              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2 text-blue-700">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">Response Time</span>
                </div>
                <p className="text-sm text-blue-600 mt-1">
                  We typically respond to all inquiries within 24 hours during business days. 
                  For urgent matters, please call our main office directly.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

    </div>
  );
};

export default Contact;